import VLCarousel, { VLCarouselPaginationProps } from "../index";
import { cloneElement, useRef, useState, MouseEvent } from "react";
import { useSelector } from "react-redux";
import styles from "./style.module.scss";
import ImageIcon from "assets/icons/imageIcon";
import useImageDragAndZoom from "hooks/useImageDragAndZoom";
import ZoomControl from "views/pages/DataPage/ImageView/ImageSection/ZoomControl";
import VLFloatGroup from "../../VLFloatGroup";
import ReviewFloatingDetails, {
  FloatingDetails,
} from "views/components/LabelPropagation/ReviewFloatingDetails";
import {
  ActionItem,
  PreviewBorderColor,
} from "views/pages/DataPage/CardsView/DataList";
import { getVLColorByActionColor } from "views/pages/DataPage/CardsView/ReviewList/utils";
import {
  ROISquare,
  roiCornerRadius,
} from "../../../components/ROISquare/ROISquare";
import { getImageDisplayOptions } from "redux/SingleDataset/selectors";
import {
  DefectAreaMask,
  OverlayControls,
  useDefectAreaOverlay,
} from "views/components/DefectAreaOverlay";

const MIN_ZOOM_LEVEL = 100;
const MAX_ZOOM_LEVEL = 500;

export interface ImageObj {
  src: string;
  title: string;
  borderColor?: string;
  previewBorderColor?: PreviewBorderColor;
  opacity?: number;
  actionList?: ActionItem[];
  floatingDetails?: FloatingDetails;
  previewFloatingDetails?: FloatingDetails;
}
export interface ImagesCarouselPagination extends VLCarouselPaginationProps {
  /** all the images, loaded or not */
  totalCount?: number;
}

export interface VLImagesCarouselPreviewProps {
  selectedItem?: number;
  imagesList: ImageObj[];
  onActionClicked?: (res: any) => void;
  afterChange: (e: number) => void;
  pagination?: ImagesCarouselPagination;
}

export default function VLImagesCarouselPreview({
  imagesList,
  selectedItem,
  onActionClicked,
  afterChange,
  pagination,
}: VLImagesCarouselPreviewProps) {
  const [selectedIndex, setSelectedIndex] = useState<number>(selectedItem || 0);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const { centerImageHandler, setZoomTo, imageZoomInLevel } =
    useImageDragAndZoom(MAX_ZOOM_LEVEL, false, imgContainerRef, MIN_ZOOM_LEVEL);
  const imgRef = useRef<HTMLImageElement>(null);
  // Displayed px per original image px (the image is rendered with object-fit: cover)
  const [displayScale, setDisplayScale] = useState(0);
  const { roiSquareSize: roiSize, centerCropSize } = useSelector(
    getImageDisplayOptions,
  );
  const { roiVisible, maskVisible, controls } = useDefectAreaOverlay();
  const roiSquareSize = roiSize * displayScale;
  const defectAreaSize = centerCropSize * displayScale;

  const contentList = imagesList.map((image) => (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      {image.previewFloatingDetails && (
        <ReviewFloatingDetails
          floatingDetails={image.previewFloatingDetails}
          className={styles.previewFloatingDetails}
        />
      )}
      <img
        src={image.src}
        title={image.title}
        alt={image.title}
        className={styles.contentImg}
      />
    </div>
  ));

  const onLoad = (e: any) => {
    const { naturalWidth, naturalHeight } = e.target;
    const img = imgRef.current;
    if (!img || !naturalWidth || !naturalHeight) return;
    setDisplayScale(
      Math.max(
        img.clientWidth / naturalWidth,
        img.clientHeight / naturalHeight,
      ),
    );
  };

  const isOver = imagesList.length <= selectedIndex;
  const selected = isOver ? imagesList[0] : imagesList[selectedIndex];
  const selectedImage = (
    <img
      src={selected.src}
      title={selected.title}
      alt={selected.title}
      className={styles.contentImg}
      onLoad={onLoad}
      ref={imgRef}
    />
  );
  const floatingDetails = selected?.floatingDetails;
  const actionList = selected?.actionList;
  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>
          <ImageIcon />
          {selected.title.split("/").pop() || selected.title}
        </div>
        <OverlayControls
          roiSize={roiSize}
          marginSize={centerCropSize}
          {...controls}
        />
      </div>
      <div className={styles.imageContainer}>
        {actionList && (
          <div className={styles.actionsListWrapper}>
            <div className={styles.actionsList}>
              <VLFloatGroup
                actionList={actionList.map((action): ActionItem => {
                  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
                    if (!action.action.onClick) return;
                    Promise.resolve(action.action.onClick(e)).then((res) => {
                      onActionClicked?.({ index: selectedIndex, res });
                    });
                  };

                  return {
                    ...action,
                    action: {
                      type: action.action.type,
                      text: action.action.text,
                      icon: action.action.icon,
                      tooltip: action.action.tooltip,
                      danger: action.action.danger,
                      className: action.action.className,
                      onClick: onClick,
                      color: action.action.color,
                    },
                  };
                })}
                placement="topRight"
                buttonSize="large"
              />
            </div>
          </div>
        )}

        <div ref={imgContainerRef} className={styles.draggableContainer}>
          <div
            className={styles.preview}
            style={{
              border: selected.previewBorderColor
                ? `${getVLColorByActionColor[selected.previewBorderColor as PreviewBorderColor]} 3px solid`
                : "none",
              opacity: selected.opacity + "%",
            }}
          >
            {floatingDetails && (
              <div className={styles.floatingDetails}>
                <ReviewFloatingDetails floatingDetails={floatingDetails} />
              </div>
            )}
            {maskVisible && (
              <DefectAreaMask
                marginSize={defectAreaSize}
                marginRadius={roiCornerRadius(roiSquareSize)}
              />
            )}
            {<ROISquare size={roiVisible ? roiSquareSize : 0} />}
            {cloneElement(selectedImage, { draggable: false })}
          </div>
        </div>

        <ZoomControl
          maxZoom={MAX_ZOOM_LEVEL}
          zoomLevel={imageZoomInLevel}
          onZoomLevelChange={setZoomTo}
          onCenterImageClick={centerImageHandler}
          className={styles.zoomController}
        />
      </div>
      <VLCarousel
        totalText={"Additional Images"}
        contentList={contentList}
        selectedItem={selectedItem}
        {...pagination}
        afterChange={(e) => {
          setSelectedIndex(e);
          afterChange(e);
        }}
      />
    </div>
  );
}
